import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

import type { SharedPlotComponent } from '@revelio/iso-utility';

export type ReplotsChartName = SharedPlotComponent;

export interface ChartDataEntry {
  component?: ReplotsChartName;
  props?: Record<string, unknown>;
  title?: string;
  markdown?: string;
}

interface SharedReportDataContextValue {
  updateChartData: (itemId: string, patch: Partial<ChartDataEntry>) => void;
  getAllChartData: () => Record<string, ChartDataEntry>;
}

const SharedReportDataContext =
  createContext<SharedReportDataContextValue | null>(null);

export const SharedReportDataProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const chartDataRef = useRef<Record<string, ChartDataEntry>>({});

  const updateChartData = useCallback(
    (itemId: string, patch: Partial<ChartDataEntry>) => {
      chartDataRef.current[itemId] = {
        ...chartDataRef.current[itemId],
        ...patch,
      };
    },
    []
  );

  const getAllChartData = useCallback(() => ({ ...chartDataRef.current }), []);

  return (
    <SharedReportDataContext.Provider
      value={{ updateChartData, getAllChartData }}
    >
      {children}
    </SharedReportDataContext.Provider>
  );
};

export const useSharedReportData = () => useContext(SharedReportDataContext);

export const useRegisterSharedChartData = (
  itemId: string,
  patch: Partial<ChartDataEntry>
) => {
  const ctx = useSharedReportData();
  const stable = JSON.stringify(patch);
  useEffect(() => {
    if (!ctx) return;
    ctx.updateChartData(itemId, JSON.parse(stable));
  }, [ctx, itemId, stable]);
};
